"use client";

import clsx from "clsx";
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

/** Pause between a message appearing and the next step starting. */
const STEP_GAP_MS = 900;
/** How long the typing indicator stays visible before the message appears. */
const TYPING_MS = 1300;

export type ChatSequenceStep = {
  /** The fully-styled message bubble (or any row) to reveal. */
  bubble: ReactNode;
  align: "start" | "end";
  /** Show a typing indicator in this slot before the bubble appears. */
  typing?: boolean;
  /** Classes applied to the typing indicator bubble so it matches the channel style. */
  typingClassName?: string;
};

/**
 * Grace period before granting a turn, so sequences whose intersection
 * observers fire near-simultaneously (e.g. a desktop row scrolling into view)
 * are granted strictly by index instead of registration order.
 */
const GRANT_DELAY_MS = 150;

type Waiter = { index: number; start: () => void };
type TurnGroup = { locks: number; queue: Waiter[]; pumpScheduled: boolean };

/**
 * Module-level coordinator so sibling sequences (separate client islands) can
 * take turns: among in-view sequences waiting in the same group, the lowest
 * index plays first and the next one starts when it finishes.
 */
const turnGroups = new Map<string, TurnGroup>();

function getTurnGroup(name: string): TurnGroup {
  let group = turnGroups.get(name);
  if (!group) {
    group = { locks: 0, queue: [], pumpScheduled: false };
    turnGroups.set(name, group);
  }
  return group;
}

function schedulePump(group: TurnGroup) {
  if (group.pumpScheduled) return;
  group.pumpScheduled = true;
  setTimeout(() => {
    group.pumpScheduled = false;
    if (group.locks > 0 || group.queue.length === 0) return;
    group.queue.sort((a, b) => a.index - b.index);
    group.queue.shift()?.start();
  }, GRANT_DELAY_MS);
}

function requestTurn(name: string, waiter: Waiter) {
  const group = getTurnGroup(name);
  group.queue.push(waiter);
  schedulePump(group);
  return () => {
    const index = group.queue.indexOf(waiter);
    if (index !== -1) group.queue.splice(index, 1);
  };
}

function holdTurn(name: string) {
  getTurnGroup(name).locks += 1;
}

function releaseTurn(name: string) {
  const group = getTurnGroup(name);
  group.locks = Math.max(0, group.locks - 1);
  schedulePump(group);
}

function subscribeToReducedMotion(onChange: () => void) {
  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  mediaQuery.addEventListener("change", onChange);
  return () => mediaQuery.removeEventListener("change", onChange);
}

export function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeToReducedMotion,
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    () => false,
  );
}

function TypingIndicator({ className }: Readonly<{ className?: string }>) {
  return (
    <span
      className={clsx(
        "animate-message-in inline-flex items-center gap-1 px-2.5 py-2",
        className,
      )}
    >
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="animate-typing-dot h-1 w-1 rounded-full bg-current"
          style={{ animationDelay: `${dot * 0.18}s` }}
        />
      ))}
    </span>
  );
}

export type ChatSequenceProps = {
  steps: ChatSequenceStep[];
  className?: string;
  /**
   * Sequences sharing a group play one at a time, ordered by sequenceIndex.
   * Without a group, the sequence plays as soon as it scrolls into view.
   */
  sequenceGroup?: string;
  sequenceIndex?: number;
  /**
   * Fraction of the conversation that must be visible before it starts.
   * Lower this for sequences inside cropped frames (e.g. a phone cut off by
   * its card), where most of the container can never be on screen.
   */
  threshold?: number;
};

/**
 * Plays a chat conversation once it scrolls into view: each bubble pops in
 * sequentially, with an optional typing indicator shown in the slot before a
 * message "arrives". Bubble slots keep their layout space reserved so the
 * frame never reflows. With reduced motion, the conversation renders
 * statically.
 */
export function ChatSequence({
  steps,
  className,
  sequenceGroup,
  sequenceIndex = 0,
  threshold = 0.35,
}: Readonly<ChatSequenceProps>) {
  const [status, setStatus] = useState<"waiting" | "playing">("waiting");
  const [shown, setShown] = useState(0);
  const [typing, setTyping] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const done = shown >= steps.length;

  // Arm once the sequence scrolls into view.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  // Claim a turn (or start on the next tick when ungrouped).
  useEffect(() => {
    if (status !== "waiting" || !inView || reducedMotion) return;
    if (!sequenceGroup) {
      const timeout = setTimeout(() => setStatus("playing"), 0);
      return () => clearTimeout(timeout);
    }
    return requestTurn(sequenceGroup, {
      index: sequenceIndex,
      start: () => setStatus("playing"),
    });
  }, [status, inView, reducedMotion, sequenceGroup, sequenceIndex]);

  // Hold the turn while playing; release when playback ends (or on unmount).
  useEffect(() => {
    if (status !== "playing" || done || !sequenceGroup) return;
    holdTurn(sequenceGroup);
    return () => releaseTurn(sequenceGroup);
  }, [status, done, sequenceGroup]);

  // Step through the conversation, then stop — it plays only once.
  useEffect(() => {
    if (status !== "playing" || done) return;

    let timeout: ReturnType<typeof setTimeout>;
    if (typing) {
      timeout = setTimeout(() => {
        setTyping(false);
        setShown((current) => current + 1);
      }, TYPING_MS);
    } else if (steps[shown]?.typing) {
      timeout = setTimeout(() => setTyping(true), STEP_GAP_MS);
    } else {
      timeout = setTimeout(
        () => setShown((current) => current + 1),
        STEP_GAP_MS,
      );
    }
    return () => clearTimeout(timeout);
  }, [status, done, shown, typing, steps]);

  return (
    <div ref={containerRef} className={className}>
      {steps.map((step, index) => {
        const isVisible = reducedMotion || index < shown;
        const isTypingHere =
          !reducedMotion && typing && index === shown && step.typing;
        return (
          <div
            // Steps are a fixed, ordered script — index is a stable key.
            key={index}
            className="relative"
          >
            <div
              className={clsx(
                "flex w-full",
                step.align === "end"
                  ? "origin-bottom-right justify-end"
                  : "origin-bottom-left justify-start",
                isVisible ? "animate-message-in" : "invisible",
              )}
            >
              {step.bubble}
            </div>
            {/* Kept mounted and toggled via CSS so the children set stays
                stable across renders (structural changes here trip the React
                DevTools instrumentation during playback). */}
            {step.typing && (
              <div
                className={clsx(
                  "absolute top-0 flex",
                  step.align === "end" ? "right-0" : "left-0",
                  !isTypingHere && "hidden",
                )}
              >
                <TypingIndicator className={step.typingClassName} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
